import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const TimelineItem = ({ experience, index, isLast }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const formatDuration = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = endDate ? new Date(endDate) : new Date();
    const months = (end?.getFullYear() - start?.getFullYear()) * 12 + (end?.getMonth() - start?.getMonth());
    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;
    
    if (years === 0) return `${remainingMonths} month${remainingMonths !== 1 ? 's' : ''}`;
    if (remainingMonths === 0) return `${years} year${years !== 1 ? 's' : ''}`;
    return `${years} year${years !== 1 ? 's' : ''} ${remainingMonths} month${remainingMonths !== 1 ? 's' : ''}`;
  };

  const isEven = index % 2 === 0;

  return (
    <div className={`relative flex ${isEven ? 'flex-row' : 'flex-row-reverse'} items-start mb-12 lg:mb-16`}>
      {/* Timeline Line */}
      <div className="absolute left-1/2 transform -translate-x-1/2 w-0.5 bg-border h-full top-0 hidden lg:block" />
      {/* Timeline Dot */}
      <div className="absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-conversion-accent rounded-full border-4 border-background z-10 hidden lg:block" />
      {/* Content Card */}
      <motion.div
        initial={{ opacity: 0, x: isEven ? -50 : 50 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: index * 0.1 }}
        className={`w-full lg:w-5/12 ${isEven ? 'lg:pr-8' : 'lg:pl-8'}`}
      >
        <div className="bg-card border border-border rounded-xl p-6 shadow-subtle hover:shadow-elevation transition-smooth group">
          {/* Company Header */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center overflow-hidden">
                <Image 
                  src={experience?.companyLogo} 
                  alt={`${experience?.company} logo`}
                  // className="w-full h-full object-cover"
                  className="object-cover"
                />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground group-hover:text-conversion-accent transition-smooth">
                  {experience?.role}
                </h3>
                <p className="text-trust-builder font-medium">{experience?.company}</p>
                <p className="text-sm text-muted-foreground">{experience?.location}</p>
              </div>
            </div>
            
            <div className="text-right">
              <p className="text-sm font-medium text-foreground">
                {experience?.startDate} - {experience?.endDate || 'Present'}
              </p>
              <p className="text-xs text-muted-foreground">
                {formatDuration(experience?.startDate, experience?.endDate)}
              </p>
            </div>
          </div>

          {/* Employment Type & Team Size */}
          <div className="flex flex-wrap gap-2 mb-4">
            <span className="px-3 py-1 bg-muted/50 text-muted-foreground text-xs rounded-full">
              {experience?.type}
            </span>
            <span className="px-3 py-1 bg-muted/50 text-muted-foreground text-xs rounded-full">
              Team: {experience?.teamSize}
            </span>
          </div>

          {/* Brief Description */}
          <p className="text-muted-foreground mb-4 leading-relaxed">
            {experience?.description}
          </p>

          {/* Key Technologies */}
          <div className="flex flex-wrap gap-2 mb-4">
            {experience?.technologies?.slice(0, 6)?.map((tech, techIndex) => (
              <span 
                key={techIndex}
                className="px-2 py-1 bg-conversion-accent/10 text-conversion-accent text-xs rounded border border-conversion-accent/20"
              >
                {tech}
              </span>
            ))}
            {experience?.technologies?.length > 6 && (
              <span className="px-2 py-1 bg-muted/30 text-muted-foreground text-xs rounded">
                +{experience?.technologies?.length - 6} more
              </span>
            )}
          </div>

          {/* Achievements Preview */}
          <div className="mb-4">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="Trophy" size={16} className="text-cta" />
              <span className="text-sm font-medium text-foreground">Key Achievements</span>
            </div>
            <ul className="space-y-1">
              {experience?.achievements?.slice(0, 2)?.map((achievement, achIndex) => (
                <li key={achIndex} className="text-sm text-muted-foreground flex items-start">
                  <Icon name="ChevronRight" size={14} className="text-conversion-accent mt-0.5 mr-2 flex-shrink-0" />
                  {achievement}
                </li>
              ))}
            </ul>
          </div>

          {/* Expand Button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
            iconPosition="right"
            className="w-full justify-center"
          >
            {isExpanded ? 'Show Less' : 'View Details'}
          </Button>

          {/* Expanded Content */}
          <AnimatePresence>
            {isExpanded && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className="mt-6 pt-6 border-t border-border"
              >
                {/* All Achievements */}
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center">
                    <Icon name="Target" size={16} className="text-cta mr-2" />
                    All Achievements
                  </h4>
                  <ul className="space-y-2">
                    {experience?.achievements?.map((achievement, achIndex) => (
                      <li key={achIndex} className="text-sm text-muted-foreground flex items-start">
                        <Icon name="CheckCircle" size={14} className="text-success mt-0.5 mr-2 flex-shrink-0" />
                        {achievement}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Key Projects */}
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center">
                    <Icon name="Briefcase" size={16} className="text-trust-builder mr-2" />
                    Key Projects
                  </h4>
                  <div className="space-y-3">
                    {experience?.projects?.map((project, projIndex) => (
                      <div key={projIndex} className="bg-muted/30 rounded-lg p-4">
                        <h5 className="font-medium text-foreground mb-1">{project?.name}</h5>
                        <p className="text-sm text-muted-foreground mb-2">{project?.description}</p>
                        <div className="flex flex-wrap gap-1">
                          {project?.technologies?.map((tech, techIndex) => (
                            <span 
                              key={techIndex}
                              className="px-2 py-0.5 bg-background text-muted-foreground text-xs rounded"
                            >
                              {tech}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* All Technologies */}
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center">
                    <Icon name="Code" size={16} className="text-conversion-accent mr-2" />
                    Technologies Used
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {experience?.technologies?.map((tech, techIndex) => (
                      <span 
                        key={techIndex}
                        className="px-2 py-1 bg-conversion-accent/10 text-conversion-accent text-xs rounded border border-conversion-accent/20"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Testimonial */}
                {experience?.testimonial && (
                  <div className="bg-muted/20 rounded-lg p-4">
                    <div className="flex items-start space-x-3">
                      <Icon name="Quote" size={16} className="text-trust-builder mt-1 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-muted-foreground italic mb-2">
                          "{experience?.testimonial?.content}"
                        </p>
                        <div className="flex items-center space-x-2">
                          <div className="w-6 h-6 bg-muted rounded-full overflow-hidden">
                            <Image 
                              src={experience?.testimonial?.avatar} 
                              alt={experience?.testimonial?.author}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div>
                            <p className="text-xs font-medium text-foreground">
                              {experience?.testimonial?.author}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {experience?.testimonial?.role}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
      {/* Mobile Timeline Indicator */}
      <div className="w-4 h-4 bg-conversion-accent rounded-full border-4 border-background flex-shrink-0 mt-6 lg:hidden" />
    </div>
  );
};

export default TimelineItem;